import React, { useEffect, useState } from 'react';
import { Award, AlertCircle, ExternalLink, UserPlus } from 'lucide-react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import StockService from '../../Api_Module/Api_Services/StockService';
import AnalystRatingsHistory from './AnalystRatingsHistory';
import FinancialForecastCharts from './FinancialForecastCharts';

interface AnalystData {
  analystId: string;
  analyst: string;
  firm: string;
  priceTarget: number;
  upsidePercent: number;
  upsideLabel: string;
  action: string;
  date: string;
  articleUrl?: string;
}

export const AnalystCornerContent: React.FC = () => {
  const { symbol: routeSymbol } = useParams<{ symbol?: string }>();
  const reduxSymbol = useSelector((state: any) => state.app?.currentSymbol);
  const symbol = reduxSymbol || routeSymbol || '';

  const [analysts, setAnalysts] = useState<AnalystData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [sortField, setSortField] = useState<string>('date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      if (!symbol) return;
      setLoading(true);
      setError(null);
      try {
        const data = await StockService.getAnalystDashboard(symbol);
        if (!cancelled) {
          let analystList: AnalystData[] = [];

          if (Array.isArray(data)) {
            // New table endpoint returns a flat array already in near-final shape
            analystList = data.map((d: any) => ({
              analystId: d.analystId || '',
              analyst: d.analyst || d.name || '—',
              firm: d.firm || '—',
              priceTarget: d.priceTarget ?? d.currentTarget ?? 0,
              upsidePercent: typeof d.upsidePercent === 'number' ? d.upsidePercent : 0,
              upsideLabel: d.upsideLabel || (d.upsidePercent >= 0 ? 'Upside' : 'Downside'),
              action: d.action || d.actionLabel || '—',
              date: d.date || d.lastTargetDate || '',
              articleUrl: d.articleUrl || d.url || ''
            }));
          } else if (data?.analysts?.leaderboard) {
            // Old dashboard structure
            analystList = (data.analysts.leaderboard || []).map((a: any) => ({
              analystId: a.analystId || '',
              analyst: a.name || a.analyst || '—',
              firm: a.firm || '—',
              priceTarget: a.currentTarget || a.priceTarget || 0,
              upsidePercent: 0,
              upsideLabel: 'Upside',
              action: 'Reiterated',
              date: a.lastTargetDate || '',
              articleUrl: ''
            }));
          } else {
            analystList = [];
          }

          setAnalysts(analystList);
        }
      } catch (e: any) {
        if (!cancelled) {
          console.error(e);
          setError(e?.message || 'Unable to load analyst data');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    // Track ViewContent for Ads
    if (symbol) {
      import('../../services/analytics').then(({ default: Analytics }) => {
        Analytics.event('ViewContent', {
          content_type: 'product',
          content_ids: [symbol],
          content_name: `${symbol} Analyst Ratings`,
          content_category: 'Analyst Corner'
        });
      });
    }
    return () => { cancelled = true; };
  }, [symbol]);

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const sortedAnalysts = [...analysts].sort((a, b) => {
    let aVal: any = a[sortField as keyof AnalystData];
    let bVal: any = b[sortField as keyof AnalystData];

    if (sortField === 'date') {
      aVal = new Date(aVal).getTime();
      bVal = new Date(bVal).getTime();
    }

    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '—';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' });
  };

  const getPositionLabel = (upside: number): { label: string; color: string } => {
    if (upside > 5) return { label: 'BUY', color: '!text-blue-500' };
    if (upside < -5) return { label: 'SELL', color: '!text-red-500' };
    return { label: 'HOLD', color: '!text-gray-500' };
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-base font-semibold text-theme-primary flex items-center gap-2">
          <Award className="text-amber-400" size={18} />
          Analyst Corner - {symbol} - Expert analysts' price targets and ratings
        </h2>
        {loading && (
          <div className="text-xs text-blue-400 mt-2">Loading analyst data...</div>
        )}
        {error && (
          <div className="text-xs text-rose-400 mt-2 flex items-center gap-2">
            <AlertCircle size={14} />
            {error}
          </div>
        )}
      </div>

      <div className="space-y-6">
        {/* Top row: Charts side by side with 40/60 split */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-2">
          <div className="lg:col-span-2">
            <AnalystRatingsHistory />
          </div>
          <div className="lg:col-span-3">
            <FinancialForecastCharts />
          </div>
        </div>

        {/* Bottom row: Full width analyst table */}
        <div className="bg-theme-secondary/30 rounded-lg border border-theme-tertiary/30 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-theme-tertiary/20 border-b border-theme-tertiary/30">
                <tr>
                  <th
                    className="px-3 py-2 text-left text-[10px] font-semibold text-theme-primary uppercase tracking-wider cursor-pointer hover:bg-theme-tertiary/30"
                    onClick={() => handleSort('analyst')}
                  >
                    <div className="flex items-center gap-1">
                      Analyst
                      {sortField === 'analyst' && (
                        <span className="text-blue-400">{sortDirection === 'asc' ? '▲' : '▼'}</span>
                      )}
                      {sortField !== 'analyst' && (
                        <span className="text-gray-500">▲</span>
                      )}
                    </div>
                  </th>
                  <th
                    className="px-3 py-2 text-left text-[10px] font-semibold text-theme-primary uppercase tracking-wider cursor-pointer hover:bg-theme-tertiary/30"
                    onClick={() => handleSort('firm')}
                  >
                    <div className="flex items-center gap-1">
                      Firm
                      {sortField === 'firm' && (
                        <span className="text-blue-400">{sortDirection === 'asc' ? '▲' : '▼'}</span>
                      )}
                      {sortField !== 'firm' && (
                        <span className="text-gray-500">▲</span>
                      )}
                    </div>
                  </th>
                  <th
                    className="px-3 py-2 text-left text-[10px] font-semibold text-theme-primary uppercase tracking-wider cursor-pointer hover:bg-theme-tertiary/30"
                    onClick={() => handleSort('priceTarget')}
                  >
                    <div className="flex items-center gap-1">
                      Target
                      {sortField === 'priceTarget' && (
                        <span className="text-blue-400">{sortDirection === 'asc' ? '▲' : '▼'}</span>
                      )}
                      {sortField !== 'priceTarget' && (
                        <span className="text-gray-500">▲</span>
                      )}
                    </div>
                  </th>
                  <th
                    className="px-3 py-2 text-left text-[10px] font-semibold text-theme-primary uppercase tracking-wider cursor-pointer hover:bg-theme-tertiary/30"
                    onClick={() => handleSort('upsidePercent')}
                  >
                    <div className="flex items-center gap-1">
                      Position
                      {sortField === 'upsidePercent' && (
                        <span className="text-blue-400">{sortDirection === 'asc' ? '▲' : '▼'}</span>
                      )}
                      {sortField !== 'upsidePercent' && (
                        <span className="text-gray-500">▲</span>
                      )}
                    </div>
                  </th>
                  <th
                    className="px-3 py-2 text-left text-[10px] font-semibold text-theme-primary uppercase tracking-wider cursor-pointer hover:bg-theme-tertiary/30"
                    onClick={() => handleSort('upsidePercent')}
                  >
                    <div className="flex items-center gap-1">
                      Upside
                      {sortField === 'upsidePercent' && (
                        <span className="text-blue-400">{sortDirection === 'asc' ? '▲' : '▼'}</span>
                      )}
                      {sortField !== 'upsidePercent' && (
                        <span className="text-gray-500">▲</span>
                      )}
                    </div>
                  </th>
                  <th
                    className="px-3 py-2 text-left text-[10px] font-semibold text-theme-primary uppercase tracking-wider cursor-pointer hover:bg-theme-tertiary/30"
                    onClick={() => handleSort('date')}
                  >
                    <div className="flex items-center gap-1">
                      Date
                      {sortField === 'date' && (
                        <span className="text-blue-400">{sortDirection === 'asc' ? '▲' : '▼'}</span>
                      )}
                      {sortField !== 'date' && (
                        <span className="text-gray-500">▲</span>
                      )}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-theme-tertiary/20">
                {sortedAnalysts.length > 0 ? (
                  sortedAnalysts.map((analyst, idx) => {
                    const position = getPositionLabel(analyst.upsidePercent);

                    return (
                      <tr
                        key={idx}
                        className="hover:bg-theme-tertiary/10 transition-colors"
                      >
                        {/* Analyst Profile */}
                        <td className="px-3 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br active-bg flex items-center justify-center text-theme-primary text-xs font-bold">
                              {analyst.analyst.charAt(0)}
                            </div>
                            <div className="text-xs font-semibold text-blue-400 hover:underline cursor-pointer truncate max-w-[100px]">
                              {analyst.analyst}
                            </div>
                          </div>
                        </td>

                        {/* Firm */}
                        <td className="px-3 py-3 text-xs text-theme-primary truncate max-w-[120px]">
                          {analyst.firm}
                        </td>

                        {/* Price Target */}
                        <td className="px-3 py-3 text-xs font-semibold text-theme-primary">
                          ${analyst.priceTarget}
                        </td>

                        {/* Position */}
                        <td className="px-3 py-3">
                          <span
                            className="text-sm font-semibold"
                            style={{
                              color: position.label === 'BUY' ? '#3B82F6' :
                                position.label === 'SELL' ? '#EF4444' :
                                  '#9CA3AF'
                            }}
                          >
                            {position.label}
                          </span>
                        </td>

                        {/* Upside/Downside */}
                        <td className="px-3 py-3">
                          <div
                            className={`text-sm font-semibold ${analyst.upsidePercent > 0
                                ? 'text-emerald-400'
                                : analyst.upsidePercent < 0
                                  ? 'text-rose-400'
                                  : 'text-theme-muted'
                              }`}
                          >
                            {analyst.upsidePercent > 0 ? '+' : ''}
                            {analyst.upsidePercent.toFixed(2)}%
                          </div>
                          <div
                            className={`text-[10px] ${analyst.upsidePercent > 0
                                ? 'text-emerald-400'
                                : analyst.upsidePercent < 0
                                  ? 'text-rose-400'
                                  : 'text-theme-muted'
                              }`}
                          >
                            {analyst.upsideLabel}
                          </div>
                        </td>

                        {/* Date */}
                        <td className="px-3 py-3 text-xs text-theme-secondary whitespace-nowrap">
                          {formatDate(analyst.date)}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-theme-muted">
                      {loading ? 'Loading analyst data...' : 'No analyst data available'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalystCornerContent;
